import { ForecastCalculator } from '../utils/forecastCalculations';
import * as api from '../../../api/api';

/**
 * OutcomeProcessor - Handles fetching and processing of individual outcomes
 * Preserves all existing API logic while organizing it better
 */
export class OutcomeProcessor {
  constructor() {
    this.calculator = new ForecastCalculator();
    this.apiCache = new Map();
  }

  /**
   * Process a single outcome with all its data
   * PRESERVES ALL EXISTING PROCESSING LOGIC
   */
  async processOutcome(outcome, year) {
    const cacheKey = `${outcome.id}_${year}`;
    
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log(`🔍 Processing outcome: ${outcome.name} (ID: ${outcome.id}, Project Code: ${outcome.project_code})`);
      }
      
      // Step 1: Fetch data (with existing timeout logic)
      const { staffingData, actualSpendData } = await this.fetchOutcomeData(outcome.id, year, cacheKey);
      
      // Step 2: Process official forecast data
      const officialForecastData = actualSpendData.officialForecast || {};
      
      // Step 3: Calculate company breakdown (existing logic)
      const { companyBreakdown, fixedCostBreakdown } = this.calculator.calculateCompanyBreakdown(staffingData);

      // Step 4: Determine if using staffing data
      const usingStaffingData = staffingData.milestones && staffingData.milestones.length > 0;

      // Step 4a: Pre-compute department breakdowns (cached on outcome — no re-fetch on filter change)
      const { deptBreakdown, deptCompanyBreakdown } = this.calculator.calculateDepartmentBreakdown(staffingData);
      const deptProjected = {};
      if (usingStaffingData) {
        Object.keys(deptBreakdown).forEach(dept => {
          // Include fixed costs tagged to this dept, plus untagged fixed costs (no dept attribution)
          const deptFixedCosts = {};
          Object.entries(fixedCostBreakdown).forEach(([name, fc]) => {
            const fcDepts = fc.departments || [];
            if (fcDepts.length === 0 || fcDepts.includes(dept)) deptFixedCosts[name] = fc;
          });
          deptProjected[dept] = this.calculateProjectedCosts(
            { [dept]: deptBreakdown[dept] },
            deptFixedCosts,
            usingStaffingData,
            officialForecastData,
            { applyVendorReplacement: false }
          );
        });
      }

      // Step 5: Calculate projected costs from pure staffing model
      const projectedCosts = this.calculateProjectedCosts(companyBreakdown, fixedCostBreakdown, usingStaffingData, officialForecastData);
      
      // Step 6: Process actual spend data (existing logic)
      const actualSpendResults = this.processActualSpendData(actualSpendData, companyBreakdown, projectedCosts.totalProjectedCost);
      
      // Step 7: Process official forecast (existing logic)
      const officialForecastResults = this.processOfficialForecast(officialForecastData);
      
      // Step 8: Calculate spend status (existing logic)
      const statusResults = this.calculator.calculateSpendStatus({
        ...outcome,
        ...projectedCosts,
        ...actualSpendResults,
        ...officialForecastResults
      });
      
      const eoyEstimate = this.computeEoyEstimate(
        actualSpendResults.monthlyActual,
        projectedCosts.monthlyProjected,
        actualSpendResults.latestActualSpendMonth
      );

      const ppmEoyEstimate = this.computeEoyEstimate(
        actualSpendResults.monthlyActual,
        projectedCosts.monthlyStaffingProjected,
        actualSpendResults.latestActualSpendMonth
      );

      return {
        ...outcome,
        ...projectedCosts,
        ...actualSpendResults,
        ...officialForecastResults,
        ...statusResults,
        usingStaffingData,
        usedOfficialForecast: projectedCosts.usedOfficialForecast || false,
        dataSource: usingStaffingData ? 'staffing' : (projectedCosts.usedOfficialForecast ? 'official_forecast' : 'none'),
        hasUploadedSpendData: actualSpendResults.hasUploadedSpendData || false,
        spendDataSource: actualSpendResults.hasUploadedSpendData ? 'uploaded' : 'none',
        companyBreakdown,
        fixedCostBreakdown,
        deptBreakdown,
        deptCompanyBreakdown: deptCompanyBreakdown || {},
        deptProjected,
        eoyEstimate,
        ppmEoyEstimate,
        companyStaffingBreakdown: projectedCosts.companyStaffingBreakdown || {},
      };
      
    } catch (error) {
      console.error(`Error processing outcome ${outcome.id}:`, error);
      return this.createEmptyOutcome(outcome);
    }
  }

  /**
   * Fetch outcome data with timeout and caching
   * PRESERVES ALL EXISTING API LOGIC
   */
  async fetchOutcomeData(outcomeId, year, cacheKey) {
    if (this.apiCache.has(cacheKey)) {
      const cached = this.apiCache.get(cacheKey);
      if (process.env.NODE_ENV === 'development' && outcomeId === '2e66cd5c-2469-4284-a881-7aa4c0990903') {
        console.log(`📦 Using cached data for ${outcomeId}:`, { 
          staffingData: cached.staffingData, 
          actualSpendData: cached.actualSpendData 
        });
      }
      return cached;
    }

    const timeoutPromise = (promise, timeout = 5000) => {
      return Promise.race([
        promise,
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout')), timeout)
        )
      ]);
    };
    
            const [staffingResponse, spendResponse] = await Promise.allSettled([
      timeoutPromise(
        api.getStaffingData(outcomeId).catch(err => {
          return { data: {} };
        }), 3000
      ),
            timeoutPromise(
        api.getSpendData(outcomeId, year).then(data => {
          if (process.env.NODE_ENV === 'development' && (outcomeId === '2e66cd5c-2469-4284-a881-7aa4c0990903' || data.hasUploadedData)) {
            console.log(`🔍 Spend data for outcome ${outcomeId}:`, {
              hasUploadedData: data.hasUploadedData,
              totalActual: data.totalActual,
              monthlySpendKeys: data.monthlySpend ? Object.keys(data.monthlySpend) : 'none',
              companySpendKeys: data.companySpend ? Object.keys(data.companySpend) : 'none',
              dataSize: JSON.stringify(data).length
            });
          }
          return data || {};
        }).catch(err => {
          if (process.env.NODE_ENV === 'development' && outcomeId === '2e66cd5c-2469-4284-a881-7aa4c0990903') {
            console.log(`❌ Error fetching spend data for outcome ${outcomeId}:`, err);
          }
          return {};
        }), 2000
      )
    ]);

        const spendData = spendResponse.status === 'fulfilled' ? (spendResponse.value || {}) : {};

    const data = {
      staffingData: staffingResponse.status === 'fulfilled' ?
        (staffingResponse.value?.data || staffingResponse.value || {}) : {},
      actualSpendData: spendData
    };

            if (process.env.NODE_ENV === 'development') {
      const hasAnySpendData = data.actualSpendData.hasUploadedData || 
        (data.actualSpendData.monthlySpend && Object.keys(data.actualSpendData.monthlySpend).length > 0) ||
        data.actualSpendData.totalActual > 0;
      
      const hasAnyForecastData = data.actualSpendData.officialForecast ||
        data.actualSpendData.monthlyForecast ||
        data.actualSpendData.hasUploadedForecastData ||
        data.actualSpendData.totalForecast > 0;
      
                // Always log for the test outcome or if there's any data
      if (hasAnySpendData || hasAnyForecastData || outcomeId === '2e66cd5c-2469-4284-a881-7aa4c0990903') {
        console.log(`📊 DETAILED SPEND DATA for ${outcomeId} (Year: ${year}):`);
        console.log('  📋 Basic Info:', {
          hasUploadedData: data.actualSpendData.hasUploadedData,
          totalActual: data.actualSpendData.totalActual,
          requestedYear: year,
          dataYear: data.actualSpendData.year,
          fromCache: this.apiCache.has(cacheKey),
          cacheKey: cacheKey
        });
        console.log('  📅 Monthly Data:', data.actualSpendData.monthlySpend);
        console.log('  🏢 Company Data:', data.actualSpendData.companySpend);
        console.log('  🔧 All Keys:', Object.keys(data.actualSpendData));
        console.log('  📦 FULL RAW RESPONSE:', data.actualSpendData);
      } else {
        // Even log empty responses for the test outcome
        if (outcomeId === '2e66cd5c-2469-4284-a881-7aa4c0990903') {
          console.log(`📊 NO SPEND DATA for ${outcomeId} (Year: ${year}):`, {
            emptyResponse: true,
            dataKeys: Object.keys(data.actualSpendData),
            fromCache: this.apiCache.has(cacheKey),
            cacheKey: cacheKey,
            RAW_DATA: data.actualSpendData
          });
        }
      }
    }
    
    this.apiCache.set(cacheKey, data);
    return data;
  }

  /**
   * Calculate projected costs from pure staffing model.
   * Official forecast never overwrites staffing data here — it is displayed separately.
   * The official forecast fallback only applies when there is no staffing data at all.
   */
  calculateProjectedCosts(companyBreakdown, fixedCostBreakdown, usingStaffingData, officialForecastData = {}, { applyVendorReplacement = true } = {}) {
    // Combined totals: official replaces all vendors in uploaded forecast; others stay on staffing model
    let monthlyProjected = Array(12).fill(0);
    let monthlyCapex = Array(12).fill(0);
    let monthlyOpex = Array(12).fill(0);
    let totalProjectedCost = 0;
    let totalCapex = 0;
    let totalOpex = 0;

    // Pure staffing-model totals (before official replaces any vendor) — used for PPM comparison
    let monthlyStaffingProjected = Array(12).fill(0);
    let totalStaffingCost = 0;
    let totalStaffingCapex = 0;
    let totalStaffingOpex = 0;
    // Snapshot of original (pre-mutation) staffing data per vendor for comparison display
    let companyStaffingBreakdown = {};

    const hasOfficialForecast = officialForecastData &&
      (officialForecastData.hasUploadedForecastData ||
       officialForecastData.uploadedForecast ||
       officialForecastData.fromUpload ||
       officialForecastData.hasOfficialForecast);

    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 Calculating projected costs (pure staffing model):', {
        hasOfficialForecast,
        usingStaffingData,
        applyVendorReplacement,
        companyCount: Object.keys(companyBreakdown).length,
        fixedCostCount: Object.keys(fixedCostBreakdown).length
      });
    }

    if (usingStaffingData && (Object.keys(companyBreakdown).length > 0 || Object.keys(fixedCostBreakdown).length > 0)) {
      const vendorMonthlyForecast = officialForecastData.companyMonthlyForecast || {};
      const hasAnyVendorForecast = applyVendorReplacement && hasOfficialForecast && Object.keys(vendorMonthlyForecast).length > 0;

      // Legacy fallback: if upload has no per-vendor breakdown, fall back to CA-only aggregate
      const caMonthlySource = vendorMonthlyForecast['CA'] || (!hasAnyVendorForecast && officialForecastData.monthlyForecast);

      if (hasOfficialForecast && (hasAnyVendorForecast || caMonthlySource)) {
        // Snapshot ALL companies and compute pure-staffing totals BEFORE any mutation
        Object.entries(companyBreakdown).forEach(([company, companyData]) => {
          for (let i = 0; i < 12; i++) {
            monthlyStaffingProjected[i] += companyData.monthlyForecast[i] || 0;
          }
          totalStaffingCost += companyData.total || 0;
          totalStaffingCapex += companyData.capex || 0;
          totalStaffingOpex += companyData.opex || 0;
          companyStaffingBreakdown[company] = {
            total: companyData.total,
            capex: companyData.capex,
            opex: companyData.opex,
            monthlyForecast: [...(companyData.monthlyForecast || Array(12).fill(0))],
            monthlyCapex: [...(companyData.monthlyCapex || Array(12).fill(0))],
            monthlyOpex: [...(companyData.monthlyOpex || Array(12).fill(0))],
          };
        });

        // Determine which vendors to replace: either all vendors from the upload, or just CA (legacy)
        const vendorsToReplace = hasAnyVendorForecast
          ? Object.keys(vendorMonthlyForecast)
          : ['CA'];
        const monthlySources = hasAnyVendorForecast
          ? vendorMonthlyForecast
          : { CA: caMonthlySource };

        vendorsToReplace.forEach(vendor => {
          const monthlySource = monthlySources[vendor];
          if (!monthlySource) return;

          // Sum fixed costs attributed to this vendor
          const vFixedMonthly = Array(12).fill(0);
          const vFixedCapex = Array(12).fill(0);
          const vFixedOpex = Array(12).fill(0);
          Object.values(fixedCostBreakdown).forEach(fc => {
            if ((fc.company || '').toUpperCase() === vendor.toUpperCase()) {
              for (let i = 0; i < 12; i++) {
                vFixedMonthly[i] += fc.monthlyForecast[i] || 0;
                vFixedCapex[i] += fc.monthlyCapex[i] || 0;
                vFixedOpex[i] += fc.monthlyOpex[i] || 0;
              }
            }
          });

          // Build official + fixed monthly arrays (1-indexed keys → 0-indexed)
          const vOfficialMonthly = Array(12).fill(0);
          const vOfficialCapex = Array(12).fill(0);
          const vOfficialOpex = Array(12).fill(0);
          Object.entries(monthlySource).forEach(([month, amount]) => {
            const i = parseInt(month) - 1;
            if (i >= 0 && i < 12) {
              vOfficialMonthly[i] = (amount || 0) + vFixedMonthly[i];
              vOfficialCapex[i] = vFixedCapex[i];
              vOfficialOpex[i] = (amount || 0) + vFixedOpex[i];
            }
          });

          // Upsert into companyBreakdown (vendor may not exist in staffing)
          companyBreakdown[vendor] = {
            ...(companyBreakdown[vendor] || {}),
            monthlyForecast: vOfficialMonthly,
            monthlyCapex: vOfficialCapex,
            monthlyOpex: vOfficialOpex,
            total: vOfficialMonthly.reduce((s, v) => s + v, 0),
            capex: vOfficialCapex.reduce((s, v) => s + v, 0),
            opex: vOfficialOpex.reduce((s, v) => s + v, 0),
          };
        });

        // Accumulate combined totals from the now-mutated companyBreakdown
        Object.entries(companyBreakdown).forEach(([company, companyData]) => {
          for (let i = 0; i < 12; i++) {
            monthlyProjected[i] += companyData.monthlyForecast[i] || 0;
            monthlyCapex[i] += companyData.monthlyCapex[i] || 0;
            monthlyOpex[i] += companyData.monthlyOpex[i] || 0;
          }
          totalProjectedCost += companyData.total || 0;
          totalCapex += companyData.capex || 0;
          totalOpex += companyData.opex || 0;
        });
      } else {
        // No official forecast — staffing = combined
        Object.entries(companyBreakdown).forEach(([company, companyData]) => {
          for (let i = 0; i < 12; i++) {
            monthlyProjected[i] += companyData.monthlyForecast[i] || 0;
            monthlyCapex[i] += companyData.monthlyCapex[i] || 0;
            monthlyOpex[i] += companyData.monthlyOpex[i] || 0;
          }
          totalProjectedCost += companyData.total || 0;
          totalCapex += companyData.capex || 0;
          totalOpex += companyData.opex || 0;
        });
        monthlyStaffingProjected = [...monthlyProjected];
        totalStaffingCost = totalProjectedCost;
        totalStaffingCapex = totalCapex;
        totalStaffingOpex = totalOpex;
      }
    } else {
      // Fallback when no staffing data - use official forecast if available
      if (hasOfficialForecast && officialForecastData.monthlyForecast) {
        Object.entries(officialForecastData.monthlyForecast).forEach(([month, amount]) => {
          const monthIndex = parseInt(month) - 1;
          if (monthIndex >= 0 && monthIndex < 12) {
            monthlyProjected[monthIndex] = amount || 0;

            if (officialForecastData.monthlyCapex && officialForecastData.monthlyCapex[month]) {
              monthlyCapex[monthIndex] = officialForecastData.monthlyCapex[month] || 0;
            }
            if (officialForecastData.monthlyOpex && officialForecastData.monthlyOpex[month]) {
              monthlyOpex[monthIndex] = officialForecastData.monthlyOpex[month] || 0;
            } else if (!officialForecastData.monthlyCapex || !officialForecastData.monthlyCapex[month]) {
              monthlyOpex[monthIndex] = amount || 0;
            }
          }
        });

        totalProjectedCost = officialForecastData.totalForecast || 0;
        totalCapex = officialForecastData.totalCapex || 0;
        totalOpex = officialForecastData.totalOpex || (officialForecastData.totalForecast || 0);
      } else {
        totalProjectedCost = monthlyProjected.reduce((sum, val) => sum + val, 0);
        totalCapex = monthlyCapex.reduce((sum, val) => sum + val, 0);
        totalOpex = monthlyOpex.reduce((sum, val) => sum + val, 0);
      }
      monthlyStaffingProjected = [...monthlyProjected];
      totalStaffingCost = totalProjectedCost;
      totalStaffingCapex = totalCapex;
      totalStaffingOpex = totalOpex;
    }

    const usedOfficialForecast = !!hasOfficialForecast;

    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 Projected costs calculated:', {
        totalProjectedCost,
        totalCapex,
        totalOpex,
        totalStaffingCost,
        usedStaffingData: usingStaffingData,
        usedOfficialForecastForVendors: usingStaffingData && hasOfficialForecast,
        usedOfficialForecastFallback: !usingStaffingData && hasOfficialForecast
      });
    }

    return {
      monthlyProjected,
      monthlyCapex,
      monthlyOpex,
      totalProjectedCost,
      totalCapex,
      totalOpex,
      usedOfficialForecast,
      monthlyStaffingProjected,
      totalStaffingCost,
      totalStaffingCapex,
      totalStaffingOpex,
      companyStaffingBreakdown,
    };
  }

    /**
   * Process actual spend data
   * PRESERVES ALL EXISTING SPEND PROCESSING LOGIC
   */
  processActualSpendData(actualSpendData, companyBreakdown, totalProjectedCost) {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 processActualSpendData input:', {
        hasUploadedData: actualSpendData.hasUploadedData,
        totalActual: actualSpendData.totalActual,
        monthlySpend: actualSpendData.monthlySpend,
        allKeys: Object.keys(actualSpendData)
      });
    }
    
    const monthlyActual = Array(12).fill(null);
    let totalActualSpend = 0;
    let totalActualCapex = 0;
    let totalActualOpex = 0;
    
        // Handle multiple possible field names for monthly spend
    const monthlySpendData = actualSpendData.monthlySpend || actualSpendData.monthly_spend || {};
    
    if (Object.keys(monthlySpendData).length > 0) {
      Object.entries(monthlySpendData).forEach(([month, amount]) => {
        const monthIndex = parseInt(month) - 1;
        if (monthIndex >= 0 && monthIndex < 12) {
          monthlyActual[monthIndex] = amount;
        }
      });
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 Processed monthly spend data:', {
          monthCount: Object.keys(monthlySpendData).length,
          monthlySpendData,
          monthlyActual
        });
      }
    }

    const latestActualSpendMonth = this.calculator.getLatestActualSpendMonth(monthlyActual);
    const latestActualSpendMonthName = latestActualSpendMonth >= 0 ? 
      ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][latestActualSpendMonth] : null;
    
        // Check multiple possible field names for uploaded data (backend might use different names)
    const hasUploadedData = actualSpendData.hasUploadedData || 
                           actualSpendData.has_uploaded_data ||
                           actualSpendData.uploaded ||
                           (actualSpendData.totalActual !== undefined && actualSpendData.totalActual > 0) ||
                           (actualSpendData.total_actual !== undefined && actualSpendData.total_actual > 0) ||
                           (actualSpendData.companySpend && Object.keys(actualSpendData.companySpend).length > 0) ||
                           (actualSpendData.company_spend && Object.keys(actualSpendData.company_spend).length > 0);
    
    const totalActualFromBackend = actualSpendData.totalActual || actualSpendData.total_actual || 0;
    const totalCapexFromBackend = actualSpendData.totalCapex || actualSpendData.total_capex || 0;
    const totalOpexFromBackend = actualSpendData.totalOpex || actualSpendData.total_opex || 0;
    
    if (hasUploadedData && (totalActualFromBackend > 0 || totalCapexFromBackend > 0 || totalOpexFromBackend > 0)) {
      totalActualSpend = totalActualFromBackend;
      totalActualCapex = totalCapexFromBackend;
      totalActualOpex = totalOpexFromBackend;
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 Using uploaded data totals:', {
          totalActualSpend,
          totalActualCapex,
          totalActualOpex,
          detectedUploadedData: hasUploadedData,
          originalFields: {
            hasUploadedData: actualSpendData.hasUploadedData,
            totalActual: actualSpendData.totalActual,
            total_actual: actualSpendData.total_actual
          }
        });
      }
    } else {
      totalActualSpend = monthlyActual
        .filter(amount => amount !== null)
        .reduce((sum, amount) => sum + amount, 0);
      
      totalActualCapex = 0;
      totalActualOpex = 0;
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 Calculated totals from monthly data:', {
          totalActualSpend,
          monthlyActual,
          nonNullMonths: monthlyActual.filter(amount => amount !== null).length
        });
      }
    }

        // Process company actuals - handle multiple field name patterns
    let companyActuals = {};
    let companyMonthlyActuals = {};
    
    const companySpendData = actualSpendData.companySpend || actualSpendData.company_spend || {};
    const companyMonthlySpendData = actualSpendData.companyMonthlySpend || actualSpendData.company_monthly_spend || {};
    
    if (hasUploadedData && Object.keys(companySpendData).length > 0) {
      companyActuals = {};
      Object.entries(companySpendData).forEach(([company, spendData]) => {
        companyActuals[company] = {
          capex: spendData.capex || spendData.CAPEX || 0,
          opex: spendData.opex || spendData.OPEX || 0,
          total: spendData.total || spendData.Total || ((spendData.capex || spendData.CAPEX || 0) + (spendData.opex || spendData.OPEX || 0)),
          unmapped: spendData.unmapped || false,
          vendorIds: spendData.vendor_ids || []
        };
      });
      
      if (Object.keys(companyMonthlySpendData).length > 0) {
        Object.entries(companyMonthlySpendData).forEach(([company, monthlyData]) => {
          companyMonthlyActuals[company] = Array(12).fill(0);
          if (!companyActuals[company]) companyActuals[company] = { capex: 0, opex: 0, total: 0 };
          companyActuals[company].monthlyCapex = Array(12).fill(0);
          companyActuals[company].monthlyOpex  = Array(12).fill(0);
          if (monthlyData && typeof monthlyData === 'object') {
            Object.entries(monthlyData).forEach(([month, entry]) => {
              const monthIndex = parseInt(month) - 1;
              if (monthIndex >= 0 && monthIndex < 12) {
                if (entry && typeof entry === 'object') {
                  companyMonthlyActuals[company][monthIndex]         = entry.total || 0;
                  companyActuals[company].monthlyCapex[monthIndex]   = entry.capex || 0;
                  companyActuals[company].monthlyOpex[monthIndex]    = entry.opex  || 0;
                } else {
                  companyMonthlyActuals[company][monthIndex] = entry || 0;
                }
              }
            });
          }
        });
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 Processed company spend data:', {
          companyCount: Object.keys(companyActuals).length,
          companies: Object.keys(companyActuals),
          companyActuals
        });
      }
    } else {
      companyActuals = {};
      companyMonthlyActuals = {};
    }
    
    // Process fixed cost actuals
    const fixedCostActuals = {};
    if (actualSpendData.hasUploadedData && actualSpendData.fixedCostSpend) {
      Object.entries(actualSpendData.fixedCostSpend).forEach(([costName, actualData]) => {
        fixedCostActuals[costName] = {
          capex: actualData.capex || 0,
          opex: actualData.opex || 0,
          total: actualData.total || ((actualData.capex || 0) + (actualData.opex || 0))
        };
      });
    }

        return {
      monthlyActual,
      totalActualSpend,
      totalActualCapex,
      totalActualOpex,
      latestActualSpendMonth,
      latestActualSpendMonthName,
      companyActuals,
      companyMonthlyActuals,
      fixedCostActuals,
      hasUploadedSpendData: hasUploadedData,
      spendDepartment: actualSpendData.department || null,
    };
  }

  /**
   * Compute EOY landing estimate for a processed outcome.
   * Call after both processActualSpendData and calculateProjectedCosts are done.
   */
  computeEoyEstimate(monthlyActual, monthlyProjected, latestActualSpendMonth) {
    let eoyEstimate = 0;
    const cutoff = latestActualSpendMonth ?? -1;
    for (let i = 0; i < 12; i++) {
      if (i <= cutoff && cutoff >= 0) {
        eoyEstimate += monthlyActual[i] || 0;
      } else {
        eoyEstimate += monthlyProjected[i] || 0;
      }
    }
    return eoyEstimate;
  }

    /**
   * Process official forecast data
   * PROCESSES DATA FROM UPLOADED EXCEL FILES
   */
  processOfficialForecast(officialForecastData) {
    let monthlyOfficialForecast = Array(12).fill(null);
    let monthlyOfficialCapex = Array(12).fill(null);
    let monthlyOfficialOpex = Array(12).fill(null);
    let totalOfficialForecast = 0;
    let totalOfficialCapex = 0;
    let totalOfficialOpex = 0;
    let hasOfficialForecast = false;
    let companyOfficialForecast = {};
    
    // Check for markers that indicate this is uploaded data, not calculated data
    // Be more flexible in detecting uploaded forecast data
    const isUploadedForecastData = officialForecastData.hasUploadedForecastData || 
                                   officialForecastData.uploadedForecast || 
                                   officialForecastData.fromUpload ||
                                   officialForecastData.hasOfficialForecast ||
                                   // Additional checks for uploaded data
                                   (officialForecastData.monthlyForecast && 
                                    officialForecastData.totalForecast > 0) ||
                                   // Check if we have forecast data that looks like it came from upload
                                   (officialForecastData.officialForecast && 
                                    Object.keys(officialForecastData.officialForecast).length > 0);
    
        if (process.env.NODE_ENV === 'development') {
      console.log(`🔍 Official forecast data check for outcome:`, {
        hasData: Object.keys(officialForecastData).length > 0,
        isUploadedData: isUploadedForecastData,
        dataKeys: Object.keys(officialForecastData),
        monthlyForecast: officialForecastData.monthlyForecast ? 'present' : 'absent',
        totalForecast: officialForecastData.totalForecast,
        officialForecast: officialForecastData.officialForecast ? 'present' : 'absent',
        // Show the actual structure
        fullStructure: Object.keys(officialForecastData).length > 0 ? officialForecastData : 'empty',
        // Check all possible markers
        allMarkers: {
          hasUploadedForecastData: officialForecastData.hasUploadedForecastData,
          uploadedForecast: officialForecastData.uploadedForecast,
          fromUpload: officialForecastData.fromUpload,
          hasOfficialForecast: officialForecastData.hasOfficialForecast,
          monthlyForecastExists: !!officialForecastData.monthlyForecast,
          totalForecastExists: !!officialForecastData.totalForecast,
          nestedOfficialForecast: !!officialForecastData.officialForecast
        }
      });
    }
    
    // Process if this is confirmed uploaded forecast data OR if we have monthly forecast data.
    // Prefer CA-only monthly data; fall back to all-vendor aggregate for legacy uploads that
    // predate companyMonthlyForecast.
    const caMonthlyForecast =
      officialForecastData.companyMonthlyForecast?.['CA'] || officialForecastData.monthlyForecast;
    if (isUploadedForecastData && caMonthlyForecast) {
      Object.entries(caMonthlyForecast).forEach(([month, amount]) => {
        const monthIndex = parseInt(month) - 1;
        if (monthIndex >= 0 && monthIndex < 12) {
          monthlyOfficialForecast[monthIndex] = amount;
        }
      });

      totalOfficialForecast = monthlyOfficialForecast
        .filter(amount => amount !== null)
        .reduce((sum, amount) => sum + amount, 0);
      hasOfficialForecast = totalOfficialForecast > 0;

      if (process.env.NODE_ENV === 'development') {
        console.log(`✅ Processing official forecast from uploaded data (CA-only): $${totalOfficialForecast}`);
        console.log(`📊 CA monthly forecast data:`, caMonthlyForecast);
      }
    } else if (officialForecastData.officialForecast && Object.keys(officialForecastData.officialForecast).length > 0) {
      // Handle nested officialForecast structure
      const nestedForecast = officialForecastData.officialForecast;
      
      if (nestedForecast.monthlyForecast) {
        Object.entries(nestedForecast.monthlyForecast).forEach(([month, amount]) => {
          const monthIndex = parseInt(month) - 1;
          if (monthIndex >= 0 && monthIndex < 12) {
            monthlyOfficialForecast[monthIndex] = amount;
          }
        });
        
        totalOfficialForecast = monthlyOfficialForecast
          .filter(amount => amount !== null)
          .reduce((sum, amount) => sum + amount, 0);
        hasOfficialForecast = totalOfficialForecast > 0;
        
        if (process.env.NODE_ENV === 'development') {
          console.log(`✅ Processing official forecast from nested structure: $${totalOfficialForecast}`);
          console.log(`📊 Nested monthly forecast data:`, nestedForecast.monthlyForecast);
        }
      }
    } else if (process.env.NODE_ENV === 'development' && Object.keys(officialForecastData).length > 0) {
      console.log(`⚠️ Could not process forecast data:`, {
        hasMonthlyForecast: !!officialForecastData.monthlyForecast,
        isUploadedData: isUploadedForecastData,
        allMarkers: {
          hasUploadedForecastData: officialForecastData.hasUploadedForecastData,
          uploadedForecast: officialForecastData.uploadedForecast,
          fromUpload: officialForecastData.fromUpload,
          hasOfficialForecast: officialForecastData.hasOfficialForecast
        },
        dataKeys: Object.keys(officialForecastData),
        fullData: officialForecastData
      });
    }
    
        // Process CAPEX/OPEX breakdown - handle both direct and nested structures
    const capexData = officialForecastData.monthlyCapex || officialForecastData.officialForecast?.monthlyCapex;
    const opexData = officialForecastData.monthlyOpex || officialForecastData.officialForecast?.monthlyOpex;
    
    if (isUploadedForecastData && capexData) {
      Object.entries(capexData).forEach(([month, amount]) => {
        const monthIndex = parseInt(month) - 1;
        if (monthIndex >= 0 && monthIndex < 12) {
          monthlyOfficialCapex[monthIndex] = amount;
        }
      });
      totalOfficialCapex = monthlyOfficialCapex
        .filter(amount => amount !== null)
        .reduce((sum, amount) => sum + amount, 0);
    }
    
    if (isUploadedForecastData && opexData) {
      Object.entries(opexData).forEach(([month, amount]) => {
        const monthIndex = parseInt(month) - 1;
        if (monthIndex >= 0 && monthIndex < 12) {
          monthlyOfficialOpex[monthIndex] = amount;
        }
      });
      totalOfficialOpex = monthlyOfficialOpex
        .filter(amount => amount !== null)
        .reduce((sum, amount) => sum + amount, 0);
    }
    
    // If no CAPEX/OPEX breakdown provided, use CA-scoped totals when available
    if (hasOfficialForecast && totalOfficialCapex === 0 && totalOfficialOpex === 0) {
      const caForecastTotals = officialForecastData.companyForecast?.['CA'] || officialForecastData.officialForecast?.companyForecast?.['CA'];
      totalOfficialCapex = caForecastTotals?.capex ?? officialForecastData.totalCapex ?? officialForecastData.officialForecast?.totalCapex ?? 0;
      totalOfficialOpex  = caForecastTotals?.opex  ?? officialForecastData.totalOpex  ?? officialForecastData.officialForecast?.totalOpex  ?? totalOfficialForecast;
    }
    
        // For company breakdown of official forecast - handle both direct and nested structures
    const companyForecastData = officialForecastData.companyForecast || officialForecastData.officialForecast?.companyForecast;
    const companyMonthlyForecastData = officialForecastData.companyMonthlyForecast || officialForecastData.officialForecast?.companyMonthlyForecast;

    if (isUploadedForecastData && companyForecastData && Object.keys(companyForecastData).length > 0) {
      Object.entries(companyForecastData).forEach(([company, companyData]) => {
        const monthlyArr = Array(12).fill(0);
        const companyMonthly = companyMonthlyForecastData?.[company];
        if (companyMonthly) {
          Object.entries(companyMonthly).forEach(([month, amount]) => {
            const idx = parseInt(month) - 1;
            if (idx >= 0 && idx < 12) monthlyArr[idx] = amount || 0;
          });
        }
        companyOfficialForecast[company] = {
          capex: companyData.capex || 0,
          opex: companyData.opex || 0,
          total: companyData.total || (companyData.capex || 0) + (companyData.opex || 0),
          monthlyForecast: monthlyArr,
          unmapped: companyData.unmapped || false,
          includesFixedCosts: true
        };
      });
    } else if (isUploadedForecastData && hasOfficialForecast) {
      // No vendor column in upload — attribute entire forecast to CA
      companyOfficialForecast['CA'] = {
        capex: totalOfficialCapex,
        opex: totalOfficialOpex,
        total: totalOfficialForecast,
        monthlyForecast: [...monthlyOfficialForecast],
        unmapped: false,
        includesFixedCosts: true
      };
    }

    return {
      monthlyOfficialForecast,
      monthlyOfficialCapex,
      monthlyOfficialOpex,
      totalOfficialForecast,
      totalOfficialCapex,
      totalOfficialOpex,
      hasOfficialForecast,
      companyOfficialForecast
    };
  }

  /**
   * Create empty outcome structure for error cases
   * PRESERVES EXISTING ERROR HANDLING
   */
  createEmptyOutcome(outcome) {
    return {
      ...outcome,
      monthlyProjected: Array(12).fill(0),
      monthlyCapex: Array(12).fill(0),
      monthlyOpex: Array(12).fill(0),
      monthlyActual: Array(12).fill(null),
      totalProjectedCost: 0,
      totalCapex: 0,
      totalOpex: 0,
      totalActualSpend: 0,
      totalActualCapex: 0,
      totalActualOpex: 0,
      latestActualSpendMonth: -1,
      latestActualSpendMonthName: null,
      actualSpendThroughLatestMonth: 0,
      baselineForecastThroughActualMonth: 0,
      spendStatus: 'on-track',
      spendStatusText: 'On Track',
      usingStaffingData: false,
      usedOfficialForecast: false,
      dataSource: 'none',
      hasUploadedSpendData: false,
      spendDataSource: 'none',
      companyBreakdown: {},
      deptBreakdown: {},
      deptCompanyBreakdown: {},
      deptProjected: {},
      companyActuals: {},
      companyMonthlyActuals: {},
      fixedCostBreakdown: {},
      fixedCostActuals: {},
      monthlyOfficialForecast: Array(12).fill(null),
      monthlyOfficialCapex: Array(12).fill(null),
      monthlyOfficialOpex: Array(12).fill(null),
      totalOfficialForecast: 0,
      totalOfficialCapex: 0,
      totalOfficialOpex: 0,
      hasOfficialForecast: false,
      companyOfficialForecast: {},
      companyStaffingBreakdown: {},
      monthlyStaffingProjected: Array(12).fill(0),
      totalStaffingCost: 0,
      totalStaffingCapex: 0,
      totalStaffingOpex: 0,
      eoyEstimate: 0,
      ppmEoyEstimate: 0,
    };
  }

    /**
   * Clear the API cache
   */
  clearCache() {
    if (process.env.NODE_ENV === 'development') {
      console.log('🧹 OutcomeProcessor: Clearing API cache with', this.apiCache.size, 'entries');
    }
    this.apiCache.clear();
  }

    /**
   * Get cache statistics
   */
  getCacheStats() {
    return {
      size: this.apiCache.size,
      keys: Array.from(this.apiCache.keys())
    };
  }

  /**
   * Clear cache for specific outcome and year
   */
  clearCacheForOutcome(outcomeId, year) {
    const cacheKey = `${outcomeId}_${year}`;
    if (this.apiCache.has(cacheKey)) {
      this.apiCache.delete(cacheKey);
      if (process.env.NODE_ENV === 'development') {
        console.log(`🧹 OutcomeProcessor: Cleared cache for outcome ${outcomeId}, year ${year}`);
      }
    }
  }
}